// ATTENTIO — core domain types
// Attention Ecology Map + Decision Provenance Trail for patients navigating care.
// Designed around moral attention: noticing who is affected, what responsibilities arise,
// what a competent response requires, and how we will know attention was adequate.

export type NodeType = 
  | 'perspective'   // a stakeholder's lived view / moral claim
  | 'task'          // concrete care task or commitment
  | 'need'          // vulnerability, concern, or moral claim that needs attention
  | 'prediction'    // "if we do X, Y will happen"
  | 'revision'      // update or new shared understanding
  | 'tension'       // explicit productive disagreement / attention gap

export type RelationKind =
  | 'attends_to'    // this node pays attention to that
  | 'predicts'      // this prediction is about that task/need
  | 'burdens'       // this creates or carries burden for
  | 'requires'      // this task requires attention from
  | 'tensions_with' // these two realities are in tension (useful, not conflict)
  | 'revises'       // this updates / qualifies an earlier node

export type EventType =
  | 'context_created'
  | 'participant_joined'
  | 'node_added'
  | 'node_updated'
  | 'burden_reported'
  | 'abandonment_logged'
  | 'recovery_clarity_set'
  | 'tension_marked'
  | 'revision_proposed'
  | 'prediction_recorded'
  | 'consent_granted'
  | 'consent_revoked'
  | 'map_exported'
  | 'live_session_started'

export interface Participant {
  id: string
  nickname: string
  role: 'patient' | 'caregiver' | 'clinician' | 'advocate' | 'coordinator' | 'other'
  joinedAt: string
  color: string // for visual attribution
}

export interface MoralAttention {
  whoIsAffected?: string      // "Who is most affected if this is ignored or mishandled?"
  responsibility?: string     // "What responsibility does noticing this create?"
  competentResponse?: string  // "What would a good, capable response actually look like here?"
  feedbackLoop?: string       // "How will we know whether attention was adequate?"
}

export interface Burden {
  cognitive: number   // 0-10 mental load, planning, remembering
  emotional: number   // 0-10 fear, grief, anxiety, relational weight
  logistical: number  // 0-10 time, transport, scheduling, physical effort
  relational: number  // 0-10 impact on family, trust, power dynamics
}

export interface AttentionNode {
  id: string
  type: NodeType
  label: string
  description?: string
  moral: MoralAttention
  position: { x: number; y: number }
  createdBy: string // participant id
  createdAt: string
  burden?: Burden
  abandonment?: {
    stage: 'planning' | 'communication' | 'execution' | 'follow-up' | 'unknown'
    reason: string
    feltBy: string[] // participant ids
    at: string
  }
  recovery?: {
    neededAttention: string
    clarity: 'low' | 'medium' | 'high'
    byWhom: string[]
    at: string
  }
}

export interface Relation {
  id: string
  fromId: string
  toId: string
  kind: RelationKind
  notes?: string
  createdAt: string
  createdBy: string
}

export interface ProvenanceEvent {
  id: string
  ts: string
  participantId: string
  type: EventType
  payload: Record<string, any>
}

export interface ConsentRecord {
  id: string
  scope: 'personal_trail' | 'room_shared' | 'anonymized_aggregate'
  granted: boolean
  grantedAt: string
  revokedAt?: string
  note?: string
}

export interface CareContext {
  id: string
  title: string
  description?: string
  createdAt: string
  updatedAt: string
  // Local only metadata
  lastOpenedAt?: string
}

export interface ContextState {
  context: CareContext
  participants: Participant[]
  nodes: AttentionNode[]
  relations: Relation[]
  events: ProvenanceEvent[]
  consents: ConsentRecord[]
}

// For live sessions (Durable Object + demo simulator)
export interface LiveSession {
  code: string
  activeParticipants: string[] // ids
  startedAt: string
}

// Stripped structural snapshot for consented public patterns (no narrative text)
export interface PublicPatternSnapshot {
  nodeTypeCounts: Record<NodeType, number>
  avgBurdenByType: Partial<Record<NodeType, Partial<Burden>>>
  abandonmentStages: Record<string, number>
  tensionCount: number
  recoveryClarityDistribution: Record<'low'|'medium'|'high', number>
  contributedAt: string
  // No labels, no descriptions, no nicknames
}

// Event taxonomy for analytics + provenance (redacted where appropriate)
export const EVENT_TAXONOMY: Record<EventType, string> = {
  context_created: 'New care attention map started',
  participant_joined: 'Someone entered the attention circle',
  node_added: 'New attention focus placed on the map',
  node_updated: 'Attention focus revised or enriched',
  burden_reported: 'Perceived weight of a task or concern named',
  abandonment_logged: 'Point where follow-through attention dropped',
  recovery_clarity_set: 'What would make recovery possible was made explicit',
  tension_marked: 'Productive difference in attention surfaced',
  revision_proposed: 'Shared understanding updated',
  prediction_recorded: 'Expectation about future state logged',
  consent_granted: 'Explicit consent to record given',
  consent_revoked: 'Consent withdrawn',
  map_exported: 'Full provenance trail exported',
  live_session_started: 'Real-time attention session opened',
}
